import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { BiltEvent } from '../../types/event';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import PageShell from '../ui/PageShell';
import { CalendarIcon } from '@heroicons/react/24/outline';
import FeatureTip from '../onboarding/FeatureTip';

function formatEventDate(startAt: string | null): string {
  if (!startAt) return 'Date TBD';
  const d = new Date(startAt.replace(' ', 'T'));
  if (isNaN(d.getTime())) return 'Date TBD';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const EventCardSkeleton: React.FC = () => (
  <div className="card p-4 sm:p-5 animate-pulse" aria-hidden>
    <div className="h-5 w-3/5 bg-[#141414] rounded mb-3" />
    <div className="h-3.5 w-28 bg-[#141414]/80 rounded mb-2" />
    <div className="h-3.5 w-36 bg-[#141414]/60 rounded mb-4" />
    <div className="h-3 w-full bg-[#141414]/40 rounded" />
  </div>
);

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<BiltEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getPublicEvents();
        setEvents(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <PageShell width="md" contentClassName="!py-6 sm:!py-8">
        <PageHeader
          eyebrow="Compete"
          title="Events"
          description="Every event adds another chapter. Bring a build from your garage; the profile stays connected."
        />

        <FeatureTip tipId="events_directory" className="mb-4">
          Register a vehicle from your garage so event history and awards stay connected to the build.
        </FeatureTip>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" role="status" aria-live="polite">
            <span className="sr-only">Loading events…</span>
            <EventCardSkeleton />
            <EventCardSkeleton />
            <EventCardSkeleton />
            <EventCardSkeleton />
          </div>
        ) : error ? (
          <EmptyState
            icon={<CalendarIcon className="w-12 h-12" />}
            title="Couldn’t load events"
            description={error}
            action={
              <Button variant="primary" onClick={() => window.location.reload()}>
                Try again
              </Button>
            }
          />
        ) : events.length === 0 ? (
          <EmptyState
            imageSrc="/images/brand/empty-events-paddock.jpg"
            imageAlt=""
            title="No events yet"
            description="When organizers publish events, register a build from your garage. No duplicate entry."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {events.map((event) => (
              <Link
                key={event.id}
                to={`/events/${event.slug}`}
                className="card-interactive block overflow-hidden"
              >
                {event.flyer_path ? (
                  <img
                    src={apiService.getFileUrl(event.flyer_path) || event.flyer_path}
                    alt=""
                    className="w-full aspect-[16/10] object-cover border-b border-white/[0.06]"
                  />
                ) : null}
                <div className="p-4 sm:p-5">
                  <h2 className="heading-card mb-1.5">{event.title}</h2>
                  <p className="text-meta">{formatEventDate(event.start_at)}</p>
                  {(event.city || event.state) && (
                    <p className="text-meta mt-0.5">
                      {[event.city, event.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {event.description && (
                    <p className="text-muted text-sm mt-2.5 line-clamp-2 leading-relaxed">
                      {event.description}
                    </p>
                  )}
                  <p className="text-accent text-sm font-medium mt-3">Register your vehicle →</p>
                </div>
              </Link>
            ))}
          </div>
        )}
    </PageShell>
  );
};

export default EventsPage;
